import React, {ReactElement} from "react";

import {css, generateKeyId} from "/src/utils/utils";

import Image, {ImageObject} from "../../image/Image";
import Video, {VideoType} from "../../videos/video/Video";
import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import ModuleBody from "../../layouts/module-body/ModuleBody";

import * as styles from "./slim-hero.module.scss";
import {MaybeType} from "/src/types";

interface SlimHeroProps {
  bgImage?: MaybeType<ImageObject>;
  bgVideo?: MaybeType<string>;
  wistiaBgVideo?: MaybeType<string>;
  copy: MaybeType<string>;
  ctas: MaybeType<Array<CTA>>;
  eyebrow: MaybeType<string>;
  eyebrowIcon: MaybeType<ImageObject>;
  headline: MaybeType<string>;
  darkMode?: MaybeType<boolean>;
  titleWithLineBreaks: MaybeType<string>;
  useTitleWithLineBreaks: MaybeType<boolean>;
  videoBehavior?: VideoType;
  videoControls?: boolean;
}

const SlimHero = (props: SlimHeroProps): ReactElement => {
  const {
    bgImage,
    bgVideo,
    wistiaBgVideo,
    copy,
    ctas,
    eyebrow,
    eyebrowIcon,
    headline,
    darkMode,
    titleWithLineBreaks,
    useTitleWithLineBreaks,
    videoBehavior,
    videoControls,
  } = props;

  const isDarkMode = darkMode === true;

  const constructVideoComponent = () => {
    if (bgVideo == null && wistiaBgVideo == null) {
      return null;
    }

    if (wistiaBgVideo != null) {
      return (
        <div className={css(styles.slimHero__right__videoWrapper)}>
          <Video
            videoConfig={{
              wistia: {
                options: {
                  fitStrategy: "cover",
                },
              },
            }}
            videoTitle={headline!}
            type={VideoType.autoplay}
            videoSource={wistiaBgVideo}
            showControl={videoControls === true}
          />
        </div>
      );
    }

    return (
      <div className={css(styles.slimHero__right__videoWrapper)}>
        <Video
          videoTitle={headline!}
          type={videoBehavior}
          videoSource={bgVideo!}
          videoClasses={styles.slimHero__right__video}
          showControl={videoControls === true}
        />
      </div>
    );
  };

  return (
    <div
      className={css(styles.slimHero, isDarkMode ? styles.slimHeroDark : "")}
    >
      <ModuleBody classes={styles.slimHero__body}>
        <div className={styles.slimHero__left}>
          {eyebrow && (
            <div className={styles.slimHero__left__eyebrowWrapper}>
              {eyebrowIcon && (
                <Image
                  image={eyebrowIcon}
                  classes={styles.slimHero__left__eyebrowIcon}
                />
              )}
              <p className={css("eyebrow", styles.slimHero__left__eyebrow)}>
                {eyebrow}
              </p>
            </div>
          )}

          {useTitleWithLineBreaks && titleWithLineBreaks != null && (
            <h2 className={css("h2", styles.slimHero__left__headline)}>
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html:
                    titleWithLineBreaks != null
                      ? titleWithLineBreaks.replace(/(\n|\r)/g, "<br/>")
                      : "",
                }}
              />
            </h2>
          )}

          {!useTitleWithLineBreaks && headline != null && (
            <h2 className={css("h2", styles.slimHero__left__headline)}>
              {headline}
            </h2>
          )}

          {copy && (
            <p className={css("p", styles.slimHero__left__copy)}>{copy}</p>
          )}

          {/* we will also assume that the first CTA would be primary, subsequent cta should be secondary */}
          {ctas && (
            <ul className={styles.slimHero__left__ctas}>
              {ctas.map((cta, i) => {
                return (
                  <li
                    key={generateKeyId(`${cta.label.toLowerCase()}-${i}`)}
                    className={styles.slimHero__left__cta}
                  >
                    <CTAButton
                      darkMode={isDarkMode}
                      cta={cta}
                      kind={i === 0 ? "primary" : "secondary"}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className={styles.slimHero__right}>
          {!bgVideo && !wistiaBgVideo && bgImage && (
            <Image
              image={bgImage}
              objectFit="cover"
              classes={styles.slimHero__right__img}
            />
          )}

          {constructVideoComponent()}
        </div>
      </ModuleBody>
    </div>
  );
};

export default SlimHero;
